import React, { useEffect, useState } from "react";
import { FileOutput, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

export default function ImportPurchase() {
  const { can } = useAuth();
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState({});
  const [meta, setMeta] = useState({ purchase_date: new Date().toISOString().slice(0, 10), external_reference: "", notes: "" });
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/imports/ready").then((r) => setGroups(r.data));
  useEffect(() => { load(); }, []);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const totalQty = groups.filter((g) => selected[g.qc_batch_id]).reduce((s, g) => s + g.items.reduce((a, i) => a + i.available, 0), 0);

  const doPreview = async () => {
    if (!selectedIds.length) return toast.error("Pilih minimal 1 batch QC");
    try { const r = await api.post("/imports/preview", { qc_batch_ids: selectedIds, ...meta }); setPreview(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createBatch = async () => {
    try {
      const r = await api.post("/imports", { qc_batch_ids: selectedIds, ...meta });
      toast.success(`Batch import dibuat: ${r.data.number}`);
      setPreview(null); setSelected({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="import-purchase-page">
      <PageHeader title="Ready for POS Import" subtitle="Barang QC Passed yang belum diimpor">
        {can("warehouse_admin") && (
          <>
            <Button variant="outline" onClick={doPreview} data-testid="preview-import-btn" className="border-taupe text-cocoa"><Eye className="w-4 h-4 mr-1.5" /> Preview ({selectedIds.length})</Button>
          </>
        )}
      </PageHeader>

      <Panel className="p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl">
        <div><Label>Purchase Date</Label><Input type="date" value={meta.purchase_date} onChange={(e) => setMeta({ ...meta, purchase_date: e.target.value })} data-testid="import-date-input" /></div>
        <div><Label>External Reference</Label><Input value={meta.external_reference} onChange={(e) => setMeta({ ...meta, external_reference: e.target.value })} /></div>
        <div><Label>Notes</Label><Input value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} /></div>
      </Panel>

      <div className="space-y-4">
        {groups.map((g) => (
          <Panel key={g.qc_batch_id} className="p-4" >
            <div className="flex items-center gap-3 mb-3">
              <Checkbox checked={!!selected[g.qc_batch_id]} onCheckedChange={(v) => setSelected({ ...selected, [g.qc_batch_id]: v })} data-testid={`select-batch-${g.qc_batch_id}`} />
              <div>
                <div className="font-mono text-sm font-semibold text-cocoa">{g.qc_batch_number}</div>
                <div className="text-xs text-wmuted">{g.supplier} · {g.warehouse_name}</div>
              </div>
              <div className="ml-auto text-sm font-semibold text-emerald-600">{g.items.reduce((a, i) => a + i.available, 0)} pcs</div>
            </div>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead>Varian</TableHead><TableHead>SKU</TableHead><TableHead className="text-right">Available</TableHead></TableRow></TableHeader>
              <TableBody>
                {g.items.map((it) => (
                  <TableRow key={it.id}><TableCell className="text-sm">{it.product_name}</TableCell><TableCell>{it.color}/{it.size}</TableCell><TableCell className="text-xs">{it.sku}</TableCell><TableCell className="text-right font-semibold">{it.available}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </Panel>
        ))}
        {!groups.length && <Panel className="p-10 text-center text-wmuted">Tidak ada barang QC Passed yang siap diimpor.</Panel>}
      </div>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-cocoa">Preview Purchase · {preview?.line_count} baris · {preview?.total_qty} pcs</DialogTitle></DialogHeader>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>POS Product ID</TableHead><TableHead>POS Variant ID</TableHead><TableHead>Produk</TableHead><TableHead>Warna/Ukuran</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>QC Batch</TableHead></TableRow></TableHeader>
              <TableBody>
                {preview?.rows.map((r, i) => (
                  <TableRow key={i}><TableCell className="font-mono text-xs">{r.pos_product_id}</TableCell><TableCell className="font-mono text-xs">{r.pos_variant_id}</TableCell><TableCell className="text-sm">{r.product_name}</TableCell><TableCell>{r.color}/{r.size}</TableCell><TableCell className="text-right font-semibold">{r.qty_passed}</TableCell><TableCell className="font-mono text-xs">{r.qc_batch_number}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} className="border-taupe text-cocoa">Tutup</Button>
            {can("warehouse_admin") && <Button onClick={createBatch} data-testid="create-import-batch-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><FileOutput className="w-4 h-4 mr-1.5" /> Buat Batch Import</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
